import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import StudentExerciseProtection from '../../components/StudentExerciseProtection';
import { studentAPI } from '../../services/api';
import './QuizResults.css';

function QuizResults({ user, onLogout }) {
  const { exerciseId } = useParams();
  const [quizStatus, setQuizStatus] = useState(null);
  const [latestAttempt, setLatestAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const resultsRequested = useRef(false);

  useEffect(() => {
    if (resultsRequested.current) return;
    resultsRequested.current = true;
    loadResults();
  }, [exerciseId]);

  const loadResults = async () => {
    try {
      setLoading(true);
      const statusRes = await studentAPI.getQuizStatus(exerciseId);
      setQuizStatus(statusRes.data);

      if (!statusRes.data.reviewAvailable) {
        setError('Quiz answers can only be reviewed once, before returning to the dashboard.');
        return;
      }
      
      // Get the latest submitted attempt
      const submittedAttempts = statusRes.data.attempts.filter(a => a.status === 'submitted');
      if (submittedAttempts.length > 0) {
        const latest = submittedAttempts[0];
        const attemptRes = await studentAPI.getQuizAttempt(latest.id);
        setLatestAttempt(attemptRes.data);
      } else {
        setError('No quiz results are available to review.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="loading">Loading results...</div>
      </Layout>
    );
  }

  if (error || !latestAttempt) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <StudentExerciseProtection mode="quiz">
        <div className="quiz-results">
          <div className="error-message">{error || 'No quiz results found'}</div>
          <Link to="/student/dashboard" className="btn btn-secondary">
            Back to Dashboard
          </Link>
        </div>
        </StudentExerciseProtection>
      </Layout>
    );
  }

  const passed = latestAttempt.score >= quizStatus.passingScore;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="quiz-results">
        <h1>Quiz Results</h1>

        <div className={`results-summary ${passed ? 'passed' : 'failed'}`}>
          <div className="score-circle">
            <div className="score-value">{latestAttempt.score}%</div>
            <div className="score-label">{passed ? 'PASSED' : 'FAILED'}</div>
          </div>
          
          <div className="results-details">
            <p><strong>Attempt:</strong> {latestAttempt.attemptNumber}</p>
            <p><strong>Passing Score:</strong> {quizStatus.passingScore}%</p>
            <p><strong>Total Attempts:</strong> {quizStatus.attemptCount}</p>
            <p><strong>Submitted:</strong> {new Date(latestAttempt.submittedAt).toLocaleString()}</p>
          </div>
        </div>

        <div className="card">
          <h2>Question Review</h2>
          <div className="questions-review">
            {latestAttempt.questions.map((question, index) => (
              <div key={question.id} className={`review-question ${question.correct ? 'correct' : 'incorrect'}`}>
                <div className="review-header">
                  <span className="question-number">Question {index + 1}</span>
                  <span className={`result-badge ${question.correct ? 'correct' : 'incorrect'}`}>
                    {question.correct ? '✓ Correct' : '✗ Incorrect'}
                  </span>
                </div>
                
                <div className="question-text">{question.question}</div>

                {question.type === 'multiple-choice' && (
                  <div className="answer-review">
                    <div className="options-review">
                      {question.options.map((option, optIndex) => {
                        const isStudentAnswer = question.studentAnswer === optIndex;
                        const isCorrectAnswer = question.shuffledCorrectAnswer === optIndex;
                        
                        return (
                          <div 
                            key={optIndex} 
                            className={`option-review ${isCorrectAnswer ? 'correct-answer' : ''} ${isStudentAnswer && !isCorrectAnswer ? 'wrong-answer' : ''}`}
                          >
                            {isStudentAnswer && <span className="indicator">Your answer: </span>}
                            {isCorrectAnswer && <span className="indicator">Correct answer: </span>}
                            {option}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {question.type === 'fill-in-blank' && (
                  <div className="answer-review">
                    <p><strong>Your answer:</strong> {question.studentAnswer || '(no answer)'}</p>
                    <p><strong>Correct answers:</strong> {question.correctAnswers.join(', ')}</p>
                  </div>
                )}

                {question.type === 'code-reading' && (
                  <div className="answer-review">
                    <div className="options-review">
                      {question.options.map((option, optIndex) => {
                        const isStudentAnswer = question.studentAnswer === optIndex;
                        const isCorrectAnswer = question.correctAnswer === optIndex;
                        
                        return (
                          <div 
                            key={optIndex} 
                            className={`option-review ${isCorrectAnswer ? 'correct-answer' : ''} ${isStudentAnswer && !isCorrectAnswer ? 'wrong-answer' : ''}`}
                          >
                            {isStudentAnswer && <span className="indicator">Your answer: </span>}
                            {isCorrectAnswer && <span className="indicator">Correct answer: </span>}
                            {option}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="results-actions">
          <Link to="/student/dashboard" className="btn btn-secondary">
            Back to Dashboard
          </Link>
          {!passed && quizStatus.canTakeQuiz && (
            <Link to={`/student/exercises/${exerciseId}/quiz`} className="btn btn-primary">
              Retake Quiz
            </Link>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default QuizResults;
